import type { Message } from './chatStore'

/**
 * Import job/report completion notices created while `activeConversationId`
 * is `null` (e.g. an import kicked off from "Nova conversa" before any
 * conversation exists) are stamped with `conversationId: ''` — see
 * ImportFlowCards.tsx and ImportJobSupervisor.tsx. That shared falsy id is a
 * "current empty conversation" bucket, matched by MessageList's fallback
 * filter (`!m.conversationId`) on EVERY future "Nova conversa" screen, in
 * every future session — not just the one where the import happened. Once
 * the job reaches a terminal state its own watchdog stops polling, so
 * nothing ever purges the message otherwise (see importJobStore.ts's
 * `applyNotFound` comment). Treat these as fossils the moment they're no
 * longer falsy-conversationId-scoped: never persist them, and drop them the
 * moment the user leaves that specific empty-conversation session.
 */
const ORPHANABLE_IMPORT_NOTICE_TYPES = new Set([
  'import_job_summary',
  'import_report',
  // Legacy WS multi-sheet completion path (connectionStore.ts `import_complete`
  // handler) — same falsy-conversationId anti-pattern, different message shape.
  'import_complete',
])

export function isOrphanedImportNotice(message: Message): boolean {
  if (message.conversationId) return false
  try {
    const parsed = JSON.parse(message.content) as { type?: unknown }
    return typeof parsed.type === 'string' && ORPHANABLE_IMPORT_NOTICE_TYPES.has(parsed.type)
  } catch {
    return false
  }
}
